/**
 * Returns true when the given feature-info URL is served from a host we
 * trust (same host as the current web app). External WMS layers point at
 * third-party hosts and must not be rendered with the dangerous renderer.
 */
export function isTrustedFeatureUrl(url: string | undefined | null): boolean {
    if (!url) return false;
    // Non-http sources produced internally (e.g. the "vector" pseudo-url) are trusted.
    if (url === "vector") return true;
    try {
        const parsed = new URL(url, window.location.origin);
        // Relative URLs resolve to the current origin and are trusted.
        return parsed.hostname === window.location.hostname;
    } catch {
        // If it can't be parsed as an absolute URL it's a relative path -> same host.
        return true;
    }
}

const DISALLOWED_TAGS = new Set([
    'SCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'BASE', 'FORM',
    'INPUT', 'BUTTON', 'TEXTAREA', 'STYLE',
]);

/**
 * Sanitises an HTML string by removing script-capable elements, inline event
 * handlers and dangerous URL schemes. Used to render feature info coming from
 * untrusted (external WMS) servers.
 *
 * Why not just use `html-react-parser`'s `parse()` instead of sanitising?
 * `parse()` converts HTML into React elements, which does neutralise the two
 * biggest vectors for free (React never executes `<script>` tags in the element
 * tree and ignores string-valued `on*` attributes). However it is NOT a
 * sanitiser: `javascript:`/`vbscript:`/`data:` URLs in href/src, and elements
 * such as `<iframe>`, `<object>`, `<embed>`, `<form>`, `<meta>` and `<base>`
 * are rendered as-is. Because this content comes from untrusted external WMS
 * servers we must explicitly strip those, so we sanitise the string here and
 * only then hand it off for rendering. (A hardened library such as DOMPurify
 * would be an alternative, but is not currently a dependency.)
 */
export function sanitizeHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    doc.querySelectorAll('*').forEach((el) => {
        if (DISALLOWED_TAGS.has(el.tagName)) {
            el.remove();
            return;
        }
        // Strip inline event handlers and javascript: / data: URLs.
        Array.from(el.attributes).forEach((attr) => {
            const name = attr.name.toLowerCase();
            const value = attr.value.trim().toLowerCase();
            if (name.startsWith('on')) {
                el.removeAttribute(attr.name);
            } else if ((name === 'href' || name === 'src' || name === 'xlink:href')
                && (value.startsWith('javascript:') || value.startsWith('data:') || value.startsWith('vbscript:'))) {
                el.removeAttribute(attr.name);
            } else if (name === 'style' && value.includes('expression')) {
                el.removeAttribute(attr.name);
            }
        });
    });

    return doc.body.innerHTML;
}
